import React, {
  useState,
  useEffect,
  useRef,
  MouseEvent,
} from "react";
import htmlParse, { HTMLReactParserOptions } from "html-react-parser";
import FlexBox from "../../../FlexBox/FlexBox";
import {
  _LandingHighlighProductContainer,
  _LandingHighlighProductCoverContainer,
  _LandingHighlighProductUmamiRoot,
  _LandingHighlighProductContentContainer,
  _FakeContentContainer,
  _HighlightProductUmamiCoverContainerWrapper,
  _StickyBackground,
} from "./LandingHighllightProductUmami.styled";
import Button from "../../../../Foundation/Button/Button";
import Typography from "../../../../Foundation/Typography/Typography";
import CompositionItem from "../CompositionItem/CompositionItem";
import Divider from "../../../Divider/Divider";
import hightlightProductBg from "../../../../Assets/images/umami/hightlight-product-bg.jpg";
import hightlightProductBgMobile from "../../../../Assets/images/umami/hightlight-product-bg-mobile.jpg";
import umamiSushishopLogo from "../../../../Assets/images/umami/umami-sushishop-logo.svg";
import umamiLogo from "../../../../Assets/images/umami/umami-logo.svg";
import umamiSymbol from "../../../../Assets/images/umami/umami-symbole.svg";
import umamiCoverBg from "../../../../Assets/images/umami/umami-cover-bg.png";
import umamiCoverBgMobile from "../../../../Assets/images/umami/umami-cover-bg-mobile.png";
import useBreakpoint from "../../../Grid/hooks/useBreakpoint";
import { responsiveMap } from "../../../Grid/_utils/responsiveObserve";
import useOnScreen from "../../../../Hooks/useOnScreen";
import useTouchScroll from "../../../TouchScroll/useTouchScroll";
import getMobileOperatingSystem from "../../../../Utils/getMobileOperatingSystem";
import { IProductComposition } from "../LandingHighlighProduct";


type ICompositionVariant = {
  type: "box";
  list: IProductComposition[];
};

interface ILandingHighlighProductUmamiProps {
  title: string;
  description?: string;
  mentionDesktop?: string;
  ctaLabel: string;
  hideCta?: boolean;
  onClick?: () => void;
  compositions: ICompositionVariant;
  compositionLabel: string;
  price?: string;
  priceSubtitle?: string;
}

const LandingHighlighProductUmami = (
  props: ILandingHighlighProductUmamiProps
) => {
  const {
    title,
    description,
    mentionDesktop,
    ctaLabel = "Je découvre",
    hideCta = false,
    onClick,
    compositionLabel,
    compositions,
    price,
    priceSubtitle,
  } = props;

  const { mobile } = useBreakpoint();
  const [opacity, setOpacity] = useState(0);
  const [scale, setScale] = useState(1.5);
  const os = getMobileOperatingSystem();
  // const { containerRef } = useAutoSlide();
  const coverRef = useRef<HTMLDivElement>(null);

  // Add visibility detection
  const [contentRef, isContentVisible] = useOnScreen<HTMLDivElement>({
    threshold: 0.3,
  });

  // const [stickyRef, isStickyVisible] = useOnScreen<HTMLDivElement>({
  //   threshold: 0.1,
  // });
  const { containerRef: stickyRef } = useTouchScroll({
    isVisible: true,
    topOffset: 64,
    upwardOffset: 64,
    downwardOffset: 128,
    enableAutoFixScroll: false,
    beforeScroll: (direction, ref) => {
      if (direction === "up") {
        // add class up to ref - and if class up is already present return false
        if (ref.current?.classList.contains("up")) return false;
        ref.current?.classList.add("up");
        return true;
      } else {
        // remove class up to ref - and if class up is not present return false
        if (!ref.current?.classList.contains("up")) return false;
        ref.current?.classList.remove("up");
        return true;
      }
    },
  });
  useEffect(() => {
    if (isContentVisible && contentRef.current && mobile) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isContentVisible, mobile]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const triggerPoint = window.innerHeight * 0.65; // Start animation at half viewport height

      const scaleStartPoint = window.innerHeight * 0.5; // Start scale animation at 50% viewport
      const maxScroll = window.innerHeight;

      const scaleProgress = scrollTop - scaleStartPoint;
      const scaleRatio = Math.min(
        scaleProgress / (maxScroll - scaleStartPoint),
        1
      );
      const newScale = 1.5 - 0.5 * scaleRatio;

      setScale(newScale);

      if (scrollTop < triggerPoint) {
        setOpacity(0);
      } else {
        const maxScroll = window.innerHeight; // Full animation over next half of viewport
        const scrollProgress = scrollTop - triggerPoint;
        const newOpacity = Math.min(
          scrollProgress / (maxScroll - triggerPoint),
          1
        );
        setOpacity(newOpacity);
      }
    };

    window.addEventListener("scroll", handleScroll);
    // Call handleScroll once to set initial opacity
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleHashLinkClick = (e: MouseEvent, href: string) => {
    e.preventDefault();
    const targetId = href.replace("#", "");
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      const targetRect = targetElement.getBoundingClientRect();

      // // Calculate the final scroll position
      let targetPosition = window.scrollY + targetRect.top - 64;
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (
        "attribs" in domNode &&
        domNode.name === "a" &&
        domNode.attribs?.href?.startsWith("#")
      ) {
        const { href } = domNode.attribs;
        return React.createElement(
          "a",
          {
            href,
            onClick: (e: MouseEvent) => handleHashLinkClick(e, href),
            style: {
              cursor: "pointer",
              color: "white",
              textDecoration: "underline",
            },
          },
          domNode.children &&
            domNode.children[0] &&
            "data" in domNode.children[0]
            ? (domNode.children[0] as { data: string }).data
            : ""
        );
      }
    },
  };

  const topContent = (
    <FlexBox direction="column" gap={12} className="top-container">
      <Typography variant="h3umami" color="white">
        {title}
      </Typography>
      {description && (
        <Typography variant="subtitle2" color="white">
          {htmlParse(description + "", parserOptions)}
        </Typography>
      )}
    </FlexBox>
  );

  const detailsContent = (
    <_LandingHighlighProductContentContainer $os={os}>
      <_LandingHighlighProductContainer
        className="highlight-product-umami-container"
        style={{ opacity: mobile ? undefined : opacity, position: "relative" }}
      >
        <FlexBox className="highlight-product-umami-top-content">
          {topContent}
        </FlexBox>

        <FlexBox className="highlight-product-umami-bottom-content">
          <FlexBox
            className="highlight-product-umami-bottom-content-wrapper"
            direction="column"
            gap={mobile ? 12 : 24}
          >
            <FlexBox
              mt={32}
              direction="column"
              gap={mobile ? 8 : 18}
              className="composition-container"
            >
              <Typography
                variant="ui"
                color="white"
                style={{ textAlign: mobile ? "left" : undefined }}
              >
                {compositionLabel}
              </Typography>
              <FlexBox direction="column" gap={4} className="composition-list">
                {compositions.list.map((item) => {
                  return (
                    <CompositionItem
                      theme="umami"
                      key={item.id_product}
                      product={item}
                    />
                  );
                })}
              </FlexBox>
            </FlexBox>

            {/* price */}
            {price && (
              <FlexBox mt={8} gap={10} className="price-container">
                <Typography variant="h6" color="white">
                  {price}
                </Typography>
                {priceSubtitle && (
                  <>
                    <Divider direction="vertical" weight={2} />
                    <Typography variant="h6light" color="white">
                      {priceSubtitle}
                    </Typography>
                  </>
                )}
              </FlexBox>
            )}
            {/* button */}
            {!hideCta && (
              <div>
                <Button
                  variant="champagne"
                  className="landing-action-button"
                  onClick={onClick}
                >
                  {ctaLabel}
                </Button>
              </div>
            )}
          </FlexBox>
        </FlexBox>

        {!mobile && (
          <FlexBox className="highlight-product-umami-mention-content">
            <Typography variant="body2" color="white">
              {htmlParse(mentionDesktop + "")}
            </Typography>
          </FlexBox>
        )}
      </_LandingHighlighProductContainer>
    </_LandingHighlighProductContentContainer>
  );

  const coverContent = (
    <_HighlightProductUmamiCoverContainerWrapper
      ref={coverRef}
      $os={os}
      className="highlight-product-umami-cover-container-wrapper"
    >
      {/* cover */}
      <_LandingHighlighProductCoverContainer
        className="highlight-product-umami-cover-container"
        $os={os}
        $bg={{
          desktop: umamiCoverBg,
          mobile: umamiCoverBgMobile,
        }}
      >
        <FlexBox direction="column" gap={51} align="center" justify="center">
          <img
            src={umamiSymbol}
            alt="umami-symbol"
            width={35}
            height={"auto"}
          />

          <img
            className="umami-logo"
            src={umamiLogo}
            alt="umami-logo"
            width={350}
            height={"auto"}
          />
          <img
            src={umamiSushishopLogo}
            alt="umami-sushishop-logo"
            width={48}
            height={48}
            style={{ marginTop: 85 }}
          />
        </FlexBox>
      </_LandingHighlighProductCoverContainer>

      {/* content */}
      {!mobile && detailsContent}
    </_HighlightProductUmamiCoverContainerWrapper>
  );

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .widget-instance {
                overflow: visible;
            }
            @media ${responsiveMap.maxLg} {
              .default-header .header-mobile-delivery-options {
                display: none !important;
              }
            }
            `,
        }}
      ></style>

      {/* background */}
      <_StickyBackground $os={os} ref={mobile ? stickyRef : undefined}>
        <div
          className="highlight-product-umami-background"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            transform: mobile ? undefined : `scale(${scale})`,
            transformOrigin: "center",
          }}
        >
          <_LandingHighlighProductUmamiRoot
            $os={os}
            $bg={{
              desktop: hightlightProductBg,
              mobile: hightlightProductBgMobile,
            }}
          />
        </div>

        {mobile && (
          <>
            <_LandingHighlighProductUmamiRoot
              $os={os}
              $bg={{
                desktop: "none",
                mobile: "none",
              }}
            >
              {detailsContent}
            </_LandingHighlighProductUmamiRoot>
            {/* cover on mobile */}
            {coverContent}
          </>
        )}
      </_StickyBackground>

      {/* cover */}
      {!mobile && coverContent}
    </>
  );
};

export default LandingHighlighProductUmami;
